import { useContext } from 'react';
import { ajvResolver } from '@hookform/resolvers/ajv';
import { yupResolver } from '@hookform/resolvers/yup';
import { GameEntity } from '@okletsplay/core';
import { merge } from 'lodash';
import { userPaymentAccountsSelector } from 'modules/payments/store/user-payment-accounts/selectors';
import { DEFAULT_FORM_VALUES } from 'modules/tournaments/constants';
import { createLolTournamentValidationSchema } from 'modules/tournaments/hooks/use-create-lol-tournament-form';
import { CreateLolTournamentFormFields } from 'modules/tournaments/interfaces/forms';
import { useForm, UseFormReturn } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useDynamicSchemaForTournamentFromGame } from 'modules/games/hooks/use-dynamic-schema-from-game';
import { GameActivityPaymentContext } from 'modules/game-payments/providers';

interface UseCreateTournamentFormHook {
  form: UseFormReturn<CreateLolTournamentFormFields>;
}

const DATE_DIFF_IN_MINUTES = 1;

export const useCreateLolTournamentForm = (game: GameEntity): UseCreateTournamentFormHook => {
  const startedAt = new Date();
  startedAt.setMinutes(startedAt.getMinutes() + DATE_DIFF_IN_MINUTES);

  const allUserPaymentAccounts = useSelector(userPaymentAccountsSelector.all);

  const { paymentConfig, platformFeePercentage } = useContext(GameActivityPaymentContext);

  const dynamicSchema = useDynamicSchemaForTournamentFromGame(game);

  const schema = createLolTournamentValidationSchema({
    getUserAccounts: () => allUserPaymentAccounts,
    getExchangeRate: () => paymentConfig.exchangeRate,
    getPlatformFeePercentage: () => platformFeePercentage,
  });

  const form = useForm<CreateLolTournamentFormFields>({
    mode: 'onChange',
    resolver: async (fields, context, options) => {
      const yup = await yupResolver(schema)(fields, context, options);
      const ajv = await ajvResolver(dynamicSchema, { strict: false })(fields, context, options);

      return merge(yup, ajv);
    },
    defaultValues: { ...DEFAULT_FORM_VALUES, startedAt, subMatchesSettings: [], spectators: [] },
  });

  return {
    form,
  };
};
